"use client";

import "@/app/(public)/shared/tokens/colors.css";
import "@/app/(public)/shared/tokens/typography.css";
import { Button } from "@/app/(public)/shared/ui/Button";
import { CTA } from "@/app/(public)/shared/components/CTA";
import LogoWall from "@/app/(public)/shared/components/LogoWall";
import { fetchCaseStudies } from "@/lib/publications/api";
import { DEFAULT_PROFILE_IMG, resolveImageSrc } from "@/lib/publications/imageUtils";
import type { CaseStudy } from "@/lib/publications/types";
import { motion } from "framer-motion";
import { Check, ChevronDown } from "lucide-react";
import { useRouter } from "next/navigation";
import { useEffect, useMemo, useRef, useState } from "react";

// Regular Case Study Card Component
interface CaseStudyCardProps {
  title: string;
  company: string;
  type: string;
  location: string;
  logoSrc: string;
  backgroundSrc: string;
  featured?: boolean;
  onClick?: () => void;
}

const CaseStudyCard = ({
  title,
  company,
  type,
  location,
  logoSrc,
  backgroundSrc,
  featured = false,
  onClick,
}: CaseStudyCardProps) => {
  // Derived — resolve each raw URL once to a safe https src (+ error flag),
  // falling back to the placeholder when the URL is missing or unusable.
  const resolvedBackground = useMemo(() => resolveImageSrc(backgroundSrc), [backgroundSrc]);
  const resolvedLogo = useMemo(() => resolveImageSrc(logoSrc), [logoSrc]);

  // States — seed the mutable src/error from the resolved values.
  const [backgroundImgSrc, setBackgroundImgSrc] = useState(resolvedBackground.src);
  const [logoImgSrc, setLogoImgSrc] = useState(resolvedLogo.src);
  const [backgroundError, setBackgroundError] = useState(resolvedBackground.isError);
  const [logoError, setLogoError] = useState(resolvedLogo.isError);

  // Handlers
  const handleBackgroundError = () => {
    if (!backgroundError) {
      console.warn(`Failed to load background image: ${backgroundSrc}`);
      setBackgroundImgSrc(DEFAULT_PROFILE_IMG);
      setBackgroundError(true);
    }
  };

  const handleLogoError = () => {
    if (!logoError) {
      console.warn(`Failed to load logo image: ${logoSrc}`);
      setLogoImgSrc(DEFAULT_PROFILE_IMG);
      setLogoError(true);
    }
  };

  return (
    <div
      className="w-full lg:flex-1 h-[580px] sm:h-[580px] lg:h-[680px] relative rounded-2xl overflow-hidden group cursor-pointer transition-all duration-300"
      style={{
        backgroundColor: "var(--landing-bg-primary)",
        border: "2px solid var(--landing-bg-primary)",
        outline: "3px solid #1c14231a",
      }}
      onClick={onClick}
    >
      {/* Background Image with blur */}
      <div className="w-full h-full overflow-hidden">
        <img
          className="w-full h-full object-cover transition-all duration-700 group-hover:scale-110"
          src={backgroundImgSrc}
          alt={title}
          onError={handleBackgroundError}
        />
      </div>

      {/* Dark Overlay on Hover */}
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-500" />

      {/* Featured badge - Top Right */}
      {featured && (
        <div className="absolute top-4 sm:top-6 right-4 sm:right-6 z-10">
          <div className="px-3 py-1 rounded-full text-body-sm font-medium text-white bg-white/30 backdrop-blur-sm">
            Featured
          </div>
        </div>
      )}

      {/* Logo positioned at top-left */}
      <div className="absolute top-4 sm:top-6 left-4 sm:left-6 z-10">
        <img
          className="w-24 sm:w-32 lg:w-40 h-8 sm:h-10 lg:h-14 object-contain brightness-110 contrast-95"
          src={logoImgSrc}
          alt="Company logo"
          onError={handleLogoError}
        />
      </div>

      {/* Content positioned at bottom */}
      <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 lg:p-8 flex flex-col gap-4 z-10">
        <div className="bg-white/30 backdrop-blur-md rounded-lg p-3">
          <div className="flex flex-col gap-1">
            <div className="text-white/95 text-body-lg font-light">{title}</div>
            <div className="text-gray-100/90 text-body-sm font-light">
              {company} / {type} / {location}
            </div>
          </div>
        </div>

        {/* Read More Button - Bottom Right */}
        <div className="flex justify-end">
          <Button
            variant="primary"
            color="primary"
            size="sm"
            className="bg-white/20 backdrop-blur-sm border border-white/30 hover:bg-white/30 transition-all duration-300"
          >
            Read more
          </Button>
        </div>
      </div>
    </div>
  );
};

// Filter Dropdown Component
interface FilterDropdownProps {
  label: string;
  options: string[];
  selectedValues: string[];
  onSelectionChange: (values: string[]) => void;
}

const FilterDropdown = ({ label, options, selectedValues, onSelectionChange }: FilterDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleOptionToggle = (option: string) => {
    const newSelection = selectedValues.includes(option)
      ? selectedValues.filter((v) => v !== option)
      : [...selectedValues, option];
    onSelectionChange(newSelection);
  };

  const hasSelection = selectedValues.length > 0;

  return (
    <div className="relative w-full sm:w-auto" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full sm:w-auto px-3 sm:px-4 py-2 sm:py-3 rounded-full outline outline-1 outline-offset-[-1px] flex justify-center items-center gap-2 sm:gap-2.5 overflow-hidden transition-all duration-200 ${
          hasSelection ? "bg-zinc-900 text-white" : "hover:bg-zinc-50"
        }`}
        style={{
          color: hasSelection ? "#ffffff" : "var(--landing-text-primary-violet)",
          outlineColor: "var(--landing-border-primary)",
        }}
      >
        <div className="text-body">{hasSelection ? `${label} (${selectedValues.length})` : label}</div>
        <ChevronDown
          className={`w-4 h-4 sm:w-5 sm:h-5 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.2 }}
          className="absolute top-full left-0 mt-2 w-full sm:w-64 rounded-xl shadow-xl border z-50 bg-white border-gray-200"
        >
          <div className="p-2">
            <div className="text-body-sm font-medium px-3 py-2 border-b text-gray-700 border-gray-200">
              Filter by {label}
            </div>
            <div className="max-h-48 mt-2 overflow-y-auto">
              {options.map((option) => (
                <button
                  key={option}
                  onClick={() => handleOptionToggle(option)}
                  className="w-full flex items-center px-3 py-2 text-body-sm text-gray-700 rounded-lg transition-colors hover:bg-gray-50"
                  style={{
                    backgroundColor: selectedValues.includes(option) ? "#f3f4f6" : "transparent",
                  }}
                >
                  <span className="capitalize text-left flex-1">{option}</span>
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                    {selectedValues.includes(option) && <Check className="w-3 h-3 sm:w-4 sm:h-4 text-blue-600" />}
                  </div>
                </button>
              ))}
            </div>
            {selectedValues.length > 0 && (
              <div className="border-t pt-2 mt-2 border-gray-200">
                <button
                  onClick={() => onSelectionChange([])}
                  className="w-full text-body-sm px-3 py-2 text-left text-blue-600 transition-colors hover:opacity-80"
                >
                  Clear all filters
                </button>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default function CaseStudies() {
  const router = useRouter();

  // Data state
  const [caseStudies, setCaseStudies] = useState<CaseStudy[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter states
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [selectedCompanySizes, setSelectedCompanySizes] = useState<string[]>([]);
  const [selectedAttendeeCounts, setSelectedAttendeeCounts] = useState<string[]>([]);

  // Fetch case studies on component mount
  useEffect(() => {
    const loadCaseStudies = async () => {
      try {
        setLoading(true);
        setError(null);
        const studies = await fetchCaseStudies();
        setCaseStudies(studies);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load case studies");
        console.error("Error fetching case studies:", err);
      } finally {
        setLoading(false);
      }
    };

    loadCaseStudies();
  }, []);

  const listedCaseStudies = caseStudies.filter((cs) => !cs.unlisted);

  // Sort case studies: featured first, then non-featured
  const getSortedCaseStudies = () => {
    const featured = listedCaseStudies.filter((cs) => cs.featured);
    const nonFeatured = listedCaseStudies.filter((cs) => !cs.featured);
    return [...featured, ...nonFeatured];
  };

  // Get unique filter options from data
  const locationOptions = Array.from(new Set(listedCaseStudies.map((cs) => cs.venue.location)));
  const companySizeOptions = Array.from(new Set(listedCaseStudies.map((cs) => cs.company.size)));
  const attendeeCountOptions = Array.from(
    new Set(listedCaseStudies.map((cs) => cs.event.attendeeCount.toString()))
  ).sort((a, b) => parseInt(a) - parseInt(b));

  // Filter case studies based on selected filters
  const getFilteredCaseStudies = () => {
    // Start with all case studies sorted (featured first)
    let filtered = getSortedCaseStudies();

    if (selectedLocations.length > 0) {
      filtered = filtered.filter((cs) => selectedLocations.includes(cs.venue.location));
    }

    if (selectedCompanySizes.length > 0) {
      filtered = filtered.filter((cs) => selectedCompanySizes.includes(cs.company.size));
    }

    if (selectedAttendeeCounts.length > 0) {
      filtered = filtered.filter((cs) => selectedAttendeeCounts.includes(cs.event.attendeeCount.toString()));
    }

    return filtered;
  };

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-landing-bg-primary" data-section="case-studies">
        {/* Hero Section Skeleton */}
        <div className="w-full flex justify-center items-center py-12 sm:py-24">
          <div className="container-page flex flex-col gap-8 sm:gap-16 pt-16 sm:pt-32">
            <div className="flex flex-col gap-8 sm:gap-12">
              <div className="flex flex-col gap-4 sm:gap-6">
                <div className="h-12 sm:h-20 w-64 sm:w-96 bg-gray-200 rounded animate-pulse" />
                <div className="flex flex-col sm:flex-row justify-start items-start sm:items-center gap-8 sm:gap-32">
                  <div className="w-32 sm:w-44 h-4 sm:h-6 bg-gray-200 rounded animate-pulse" />
                  <div className="flex gap-4 sm:gap-8">
                    <div className="w-12 sm:w-16 h-6 sm:h-8 bg-gray-200 rounded animate-pulse" />
                    <div className="w-12 sm:w-16 h-6 sm:h-8 bg-gray-200 rounded animate-pulse" />
                    <div className="w-12 sm:w-16 h-6 sm:h-8 bg-gray-200 rounded animate-pulse" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Featured Case Studies Skeleton */}
        <div className="w-full flex justify-center items-center">
          <div className="container-page px-4 sm:px-0">
            <div className="flex flex-col lg:flex-row gap-4 sm:gap-6">
              <div className="w-full lg:flex-1 h-[580px] sm:h-[580px] lg:h-[680px] bg-gray-200 rounded-2xl animate-pulse" />
              <div className="w-full lg:flex-1 h-[580px] sm:h-[580px] lg:h-[680px] bg-gray-200 rounded-2xl animate-pulse" />
              <div className="w-full lg:flex-1 h-[580px] sm:h-[580px] lg:h-[680px] bg-gray-200 rounded-2xl animate-pulse" />
            </div>
          </div>
        </div>

        {/* All Stories Section Skeleton */}
        <div className="w-full flex justify-center items-center py-12 sm:py-24">
          <div className="container-page flex flex-col gap-8 sm:gap-12 px-4 sm:px-0">
            <div className="flex flex-col gap-4 sm:gap-6">
              <div className="h-8 sm:h-12 w-32 sm:w-48 bg-gray-200 rounded animate-pulse" />

              {/* Filters Skeleton */}
              <div className="flex flex-col gap-4 justify-between items-start">
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 w-full sm:w-auto">
                  <div className="h-8 sm:h-10 w-full sm:w-32 bg-gray-200 rounded animate-pulse" />
                  <div className="h-8 sm:h-10 w-full sm:w-32 bg-gray-200 rounded animate-pulse" />
                  <div className="h-8 sm:h-10 w-full sm:w-32 bg-gray-200 rounded animate-pulse" />
                </div>
                <div className="h-4 sm:h-6 w-16 sm:w-24 bg-gray-200 rounded animate-pulse" />
              </div>

              <div className="h-px bg-gray-200" />
            </div>

            {/* Case Study Grid Skeleton */}
            <div className="flex flex-col gap-8 sm:gap-12">
              <div className="flex flex-col lg:flex-row gap-6 sm:gap-12">
                <div className="w-full lg:flex-1 h-[600px] sm:h-[380px] lg:h-[420px] bg-gray-200 rounded-2xl animate-pulse" />
                <div className="w-full lg:flex-1 h-[600px] sm:h-[380px] lg:h-[420px] bg-gray-200 rounded-2xl animate-pulse" />
              </div>
              <div className="flex flex-col lg:flex-row gap-6 sm:gap-12">
                <div className="w-full lg:flex-1 h-[600px] sm:h-[380px] lg:h-[420px] bg-gray-200 rounded-2xl animate-pulse" />
                <div className="w-full lg:flex-1 h-[600px] sm:h-[380px] lg:h-[420px] bg-gray-200 rounded-2xl animate-pulse" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen container-page bg-landing-bg-primary px-4">
        <div className="flex items-center justify-center py-12 sm:py-24">
          <div className="text-center">
            <div className="text-body-lg text-red-600">Error: {error}</div>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  const gridCaseStudies = getFilteredCaseStudies();

  return (
    <div className="min-h-screen bg-landing-bg-primary overflow-x-hidden" data-section="case-studies">
      {/* Hero Section with Container */}
      <div className="w-full flex justify-center items-center py-12 sm:py-24 overflow-hidden">
        <div className="container-page flex flex-col gap-8 sm:gap-16 pt-16 sm:pt-32 px-4 sm:px-0">
          {/* Hero Section */}
          <div className="flex flex-col gap-8 sm:gap-12">
            <div className="flex flex-col gap-4 sm:gap-6 overflow-hidden">
              <div className="text-display text-landing-primary">Customer Stories</div>
              <div className="flex flex-col sm:flex-row justify-start items-start sm:items-center gap-8 sm:gap-32 overflow-hidden">
                <div className="md:w-44 text-body text-landing-secondary flex-shrink-0">
                  Trusted by over 180 world class companies
                </div>
                <div className="flex-1 overflow-hidden">
                  <LogoWall variant="infinite-scroll" type="featured" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* All Stories Section with Container */}
      <div className="w-full flex justify-center items-center py-12 sm:py-24">
        <div className="container-page flex flex-col gap-8 sm:gap-12 px-4 sm:px-0">
          <div className="flex flex-col gap-4 sm:gap-6">
            <div className="text-h2 text-landing-primary">All stories</div>

            {/* Filters */}
            <div className="flex flex-col gap-4 justify-between items-start">
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 w-full sm:w-auto">
                <FilterDropdown
                  label="Location"
                  options={locationOptions}
                  selectedValues={selectedLocations}
                  onSelectionChange={setSelectedLocations}
                />
                <FilterDropdown
                  label="Company Size"
                  options={companySizeOptions}
                  selectedValues={selectedCompanySizes}
                  onSelectionChange={setSelectedCompanySizes}
                />
                <FilterDropdown
                  label="Attendee Count"
                  options={attendeeCountOptions}
                  selectedValues={selectedAttendeeCounts}
                  onSelectionChange={setSelectedAttendeeCounts}
                />
              </div>

              {/* Results Counter */}
              <div className="text-body text-landing-secondary">
                {gridCaseStudies.length} {gridCaseStudies.length === 1 ? "result" : "results"}
              </div>
            </div>

            <div className="h-px landing-border-primary" />
          </div>

          {/* Case Study Grid */}
          <div className="flex flex-col gap-4 sm:gap-6">
            {gridCaseStudies.length === 0 ? (
              // No results state
              <div className="text-center py-12 sm:py-16">
                <div className="text-h3 mb-4 text-landing-primary">No case studies found</div>
                <div className="text-body-lg landing-text mb-6 text-landing-secondary">
                  Try adjusting your filters to see more results.
                </div>
                <Button
                  variant="secondary"
                  color="primary"
                  size="md"
                  onClick={() => {
                    setSelectedLocations([]);
                    setSelectedCompanySizes([]);
                    setSelectedAttendeeCounts([]);
                  }}
                >
                  Clear all filters
                </Button>
              </div>
            ) : (
              // Render grid in responsive rows
              Array.from({ length: Math.ceil(gridCaseStudies.length / 3) }).map((_, rowIndex) => (
                <div key={rowIndex} className="flex flex-col lg:flex-row gap-4 sm:gap-6">
                  {gridCaseStudies.slice(rowIndex * 3, rowIndex * 3 + 3).map((caseStudy) => (
                    <CaseStudyCard
                      key={caseStudy.id}
                      title={caseStudy.previewTitle}
                      company={caseStudy.company.name}
                      type={caseStudy.event.type}
                      location={caseStudy.venue.location}
                      logoSrc={caseStudy.company.logo.url}
                      backgroundSrc={caseStudy.heroImage.url}
                      featured={caseStudy.featured}
                      onClick={() => router.push(`/case-studies/${caseStudy.slug}`)}
                    />
                  ))}
                  {/* If incomplete row, add empty divs to maintain layout on desktop */}
                  {rowIndex === Math.ceil(gridCaseStudies.length / 3) - 1 && gridCaseStudies.length % 3 !== 0 && (
                    <>
                      {Array.from({ length: 3 - (gridCaseStudies.length % 3) }).map((_, emptyIndex) => (
                        <div key={`empty-${emptyIndex}`} className="hidden lg:block flex-1" />
                      ))}
                    </>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
      <CTA />
    </div>
  );
}
